import { usePairs } from '@/store/pairs';
import { usePrice } from '@/store/price';
import {
    Bar,
    LibrarySymbolInfo,
    ResolutionString,
    SubscribeBarsCallback,
} from 'public/charting_library/charting_library';

type ChannelToSubscriptionData = {
    subscriberUID: string;
    resolution: ResolutionString;
    lastDailyBar: Bar;
    handlers: {
        id: string;
        callback: SubscribeBarsCallback;
    }[];
    unsubscribe?: () => void; // Added unsubscribe property
};

const mapResolution: Record<number | string, number> = {
    1: 60000,
    5: 300000,
    15: 900000,
    60: 3600000,
    240: 14400000,
    1440: 86400000,
};

const channelToSubscription = new Map<string, ChannelToSubscriptionData>();

export function subscribeOnStream(
    symbolInfo: LibrarySymbolInfo,
    resolution: ResolutionString,
    onRealtimeCallback: SubscribeBarsCallback,
    subscriberUID: string,
    onResetCacheNeededCallback: () => void,
    lastDailyBar: any
) {
    const channelString = `${symbolInfo.ticker || ''}-${resolution}`;
    const handler = {
        id: subscriberUID,
        callback: onRealtimeCallback,
    };

    const existing = channelToSubscription.get(channelString);
    if (existing) {
        existing.handlers.push(handler);
        return;
    }

    const token = usePairs
        .getState()
        .data.find(
            (v) => v.symbol.toLowerCase() === symbolInfo.ticker?.toLowerCase()
        );
    if (!token) return;

    const tokenId = token.indexPrice?.toString() ?? '0';
    const unsubscribe = usePrice.subscribe(
        (state) => state.data[tokenId]?.price,
        (priceStr) => {
            if (!priceStr) return;
            const tradePrice = parseFloat(priceStr);

            if (isNaN(tradePrice)) return;

            const currentDate = Date.now();
            const sub = channelToSubscription.get(channelString);
            if (!sub || !sub.lastDailyBar) return;

            const lastBar = sub.lastDailyBar;
            const sameBucket =
                Math.floor(lastBar.time / mapResolution[resolution]) ===
                Math.floor(currentDate / mapResolution[resolution]);

            const bar = sameBucket
                ? {
                      ...lastBar,
                      time: currentDate,
                      high: Math.max(lastBar.high, tradePrice),
                      low: Math.min(lastBar.low, tradePrice),
                      close: tradePrice,
                  }
                : {
                      ...lastBar,
                      time: currentDate,
                      open: lastBar.close,
                      high: tradePrice,
                      low: tradePrice,
                      close: tradePrice,
                  };

            sub.lastDailyBar = bar;
            sub.handlers.forEach((h) => h.callback(bar));
        }
    );

    channelToSubscription.set(channelString, {
        subscriberUID: tokenId,
        resolution,
        lastDailyBar,
        handlers: [handler],
        unsubscribe, // gán để khi `unsubscribeFromStream` thì gọi luôn
    });
}

export function unsubscribeFromStream(subscriberUID: string) {
    // find a subscription with id === subscriberUID
    for (const channelString of channelToSubscription.keys()) {
        const subscriptionItem = channelToSubscription.get(channelString);
        if (
            typeof subscriptionItem === 'undefined' ||
            subscriptionItem === null
        ) {
            return;
        }

        const handlerIndex = subscriptionItem.handlers.findIndex(
            (handler) => handler.id === subscriberUID
        );

        if (handlerIndex === -1) {
            return;
        }

        // remove from handlers
        subscriptionItem.handlers.splice(handlerIndex, 1);

        if (subscriptionItem.handlers.length === 0) {
            // unsubscribe from the channel, if it was the last handler
            console.log(
                '[unsubscribeBars]: Unsubscribe from streaming. Channel:',
                channelString
            );
            channelToSubscription.delete(channelString);
            break;
        }
    }
}
